import { useResponsive } from '@/hooks';
import { useStore } from '@/zustand/store/store';
import { useTranslate } from '@tolgee/react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import AchievementListWithApi from '@/components/Achievements/CampaignCards/components/AchievementListWithApi';
import AchievementListWithApiArmies from '@/components/Achievements/CampaignCards/components/AchievementListWithApiArmies';
import TopBarArmySection from '@/components/Achievements/CampaignCards/components/TopBarArmySection';
import ArmiesSection from '@/components/CommandCenter/LeftBox/ArmiesSection';
import InventorySection from '@/components/CommandCenter/RightBox/InventorySection';
import LeaderBoardSection from '@/components/CommandCenter/RightBox/LeaderboardSection';
import LivePricesSection from '@/components/CommandCenter/RightBox/LivePricesSection';
import BadgeProgress from '../BadgeProgress';
import CustomAccordion from '../CustomAccordion';

import { Typography } from '@/components/Atoms';
import { ArmiesSvg, LivePricesSvg } from '@/components/Svg';
import InventoryTwoSvg from '@/components/Svg/InventoryTwoSvg';
import LeaderboardTwoSvg from '@/components/Svg/LeaderboardTwoSvg';
import { ITaskCategoryData } from '@/types/Achievements';

import AccordionHeader from '../AccordionHeader';
import styles from './BadgeTabs.module.scss';
import CustomToggle from './CustomToggle';

const BadgeTabs = ({ categories, activeCategory, queries, setQueries, filteredCommanderQuery, onCategoryChange, setActiveCategory, gameId }) => {
  const { t } = useTranslate('commandCenter');
  const [selectedCategory, setSelectedCategory] = useState(activeCategory);
  const [activeBadge, setActiveBadge] = useState<string | null>(null);
  const badgeItems = useRef<HTMLDivElement>(null);
  const [badgeWidth, setBadgeWidth] = useState(0);
  const [isHover, setIsHover] = useState(false);

  const rankConfig = useStore((state) => state.rankConfig);
  const progressWidths = [5, 90, 100, 120, 120, 120];
  const { isMobile, isTablet } = useResponsive();

  const handleBadgeClick = (category: ITaskCategoryData | null) => {
    const newCategoryId = category ? category.id : 'all';
    setSelectedCategory(newCategoryId);
    setActiveBadge(newCategoryId);
    setQueries((prev) => ({
      ...prev,
      category_ids: category ? [category.id] : [],
      is_missed: false,
    }));
    onCategoryChange(category);
  };

  const handleGamesClick = () => {
    setSelectedCategory(gameId);
    setActiveBadge(gameId);
    setActiveCategory('Game');
    setQueries((prev) => ({
      ...prev,
      category_ids: [gameId],
      is_missed: false,
    }));
  };

  const handleMissedClick = () => {
    setSelectedCategory('missed');
    setActiveBadge('missed');
    setQueries((prev) => ({
      ...prev,
      category_ids: [],
      is_missed: true,
    }));
  };

  useEffect(() => {
    if (badgeItems.current && categories.length > 0) {
      const badgeWidth = badgeItems.current.clientWidth / categories.length;
      setBadgeWidth(badgeWidth);
    }
  }, [categories.length]);

  useEffect(() => {
    if (queries.is_missed) {
      setSelectedCategory('missed');
      setActiveBadge('missed');
    } else if (queries.category_ids?.length) {
      const newCategory = categories.find((cat) => cat.id === queries.category_ids[0]);
      if (newCategory) {
        setSelectedCategory(newCategory.id);
        setActiveBadge(newCategory.id);
      }
    } else {
      setSelectedCategory('all');
      setActiveBadge('all');
    }
  }, [queries, categories]);

  const renderBadgeRow = () => {
    return categories.map((category, index) => {
      let progressPercentage = 0;
      const currentPoint = rankConfig.currentPoint;

      if (currentPoint > category.start && currentPoint < category.end) {
        progressPercentage = ((currentPoint - category.start) / (category.end - category.start)) * 100;
      } else if (currentPoint >= category.end) {
        progressPercentage = 100;
      }

      return (
        <BadgeProgress
          key={category.id}
          badgeSrc={category.icon}
          progressWidth={progressWidths[index] || 120}
          title={category.category_name}
          shouldShowTitle={index < 5}
          progressPercentage={progressPercentage}
          onClick={() => handleBadgeClick(category)}
          isSelected={activeBadge === category.id}
          category={category}
        />
      );
    });
  };

  return (
    <>
      <style jsx>{`
        @media (max-width: 500px) {
          .custom-grid {
            grid-template-columns: 1fr; /* Full width for screens under 500px */
          }
        }
        @media (min-width: 501px) and (max-width: 1023px) {
          .custom-grid {
            grid-template-columns: repeat(2, 1fr); /* Two columns for screens 501px and up to 1023px */
          }
        }
      `}</style>
      <div className={styles.wrapper}>
        <div className={styles.contentWrapper}>
          <div className='flex flex-col w-fit justify-center items-center gap-[4px] pl-4 cursor-pointer'>
            <CustomToggle
              options={['All', 'Games', 'Missed']}
              onToggle={(v) => {
                if (v === 'Games') {
                  return handleGamesClick();
                }

                if (v === 'Missed') {
                  handleMissedClick();
                } else {
                  handleBadgeClick(null);
                }
              }}
            />
          </div>

          {/* Badge Row */}
          <div className={classNames('w-full flex overflow-x-auto overflow-y-hidden min-h-[100px] items-center px-[50px]', styles.scroll)}>
            <div ref={badgeItems}>
              <div className='flex items-center'>{renderBadgeRow()}</div>
            </div>
          </div>
          {/* <div
            onClick={handleMissedClick}
            onMouseMove={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
            className='flex flex-row mt-3 bg-[#07B9C51A]/[0.1] border-[2px] border-[#07B9C5] p-2 justify-center rounded-md w-[120px] h-10  items-center gap-[4px] ml-4 cursor-pointer '
          >
            <MissedCommandSvg width={40} height={40} />
            <Typography size='s14ToS12' className='text-[#D9D9D9] flex items-center justify-center text-center leading-none'>
              Missed Commands
            </Typography>
          </div> */}
        </div>

        {/* Rest of your component */}
        {/* Accordion sections */}
        <div className='lg:hidden grid custom-grid gap-2 mt-2'>
          <CustomAccordion
            isAcc={isMobile || isTablet}
            title='Inventory'
            header={<AccordionHeader title={t('RIGHT_SECTION.inventorySection.title')} Icon={InventoryTwoSvg} />}
          >
            <InventorySection />
          </CustomAccordion>

          <CustomAccordion
            isAcc={isMobile || isTablet}
            title='Leaderboard'
            header={<AccordionHeader title={t('RIGHT_SECTION.specialCommandSectionTop.leaderbord')} Icon={LeaderboardTwoSvg} withPing={true} />}
          >
            <LeaderBoardSection />
          </CustomAccordion>

          <CustomAccordion
            isAcc={isMobile || isTablet}
            title='Armies'
            header={<AccordionHeader title={t('LEFT_SECTION.armiesSection.title')} Icon={ArmiesSvg} />}
          >
            <ArmiesSection />
          </CustomAccordion>

          <CustomAccordion isAcc={isMobile || isTablet} title='Live Price' header={<AccordionHeader title='Live Prices' Icon={LivePricesSvg} />}>
            <LivePricesSection />
          </CustomAccordion>
        </div>

        {/* Conditional rendering of sections */}
        <div className='rounded-md mt-3'>
          {filteredCommanderQuery && <TopBarArmySection />}
          {filteredCommanderQuery && queries.role === 'true' ? (
            <AchievementListWithApiArmies queries={queries} commanderId={filteredCommanderQuery} role={queries.role === 'true'} />
          ) : (
            <AchievementListWithApi queries={queries} role={queries.role === 'true'} />
          )}
        </div>
      </div>
    </>
  );
};

export default BadgeTabs;

export const BadgeButton = ({ onClick, isActive, Icon, label }) => {
  const [isHover, setIsHover] = useState(false);

  return (
    <div className='flex flex-col w-fit justify-start items-center gap-[4px] pl-4 cursor-pointer relative'>
      <div className='cursor-pointer' onClick={onClick} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
        <Icon width={35} height={35} color={isActive ? '#00D0AB' : isHover ? '#63fde1' : '#D9D9D9'} />
      </div>
      <div className='absolute w-full bg-[#393434] bg-opacity-50 rounded-[4px] top-16 left-0 pl-[24px] pb-[1px] text-[#909090]'>
        <Typography
          size='s14ToS12'
          className={classNames(
            'w-fit items-center justify-center capitalize cursor-pointer flex-nowrap transition-all',
            isActive ? 'text-[#00D0AB] hover:text-[#63fde1]' : 'text-white cursor-not-allowed pointer-events-none',
          )}
          onClick={onClick}
        >
          {label}
        </Typography>
      </div>
    </div>
  );
};
